import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Divider, Empty, Input, InputNumber, Select, Space, Spin, Table, Tag, Tooltip, message,
} from 'antd';
import { CaretDownOutlined, CaretRightOutlined, CopyOutlined, DeleteOutlined, PlusOutlined, PrinterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getNonStdContainers, saveNonStdContainers } from '../../services/nonstandardOps';
import { getLocations } from '../../services/storage';
import { ContainerDraft, ContainerKind, ContainerLedgerRow, ContainersView, NonStdLabel } from '../../types/nonstandardOps';
import { ConsumedTag } from './labelGroups';
import { useContainerLabelPrinter } from './containerLabels';
import { CONTAINER_TYPES } from './containerTypes';

/** 面板向外报告的状态：终审页据此显示标签，并在有未保存的修改或检查没通过时拦住"提交终审" */
export interface ContainersStatus {
  text: string;
  color: 'default' | 'success' | 'warning' | 'processing';
  /** 本地有还没保存的容器修改 */
  dirty: boolean;
  /** 已保存的容器数量（库存结算面板据此判断有没有需要预览确认的内容） */
  count: number;
  /** 已保存的容器草稿检查没通过（有阻断项） */
  hasBlockers: boolean;
}

/** 面板对外提供的操作：终审页底部的确认栏用它来保存和同步 */
export interface ContainersPanelHandle {
  /** 保存容器草稿（没有未保存的修改时直接返回 true）；失败返回 false。silent = 不弹“保存成功”的提示 */
  save: (silent?: boolean) => Promise<boolean>;
  /** 从服务器重新读取对账表和版本（有未保存的修改时不覆盖正在编辑的容器） */
  refresh: () => Promise<void>;
}

interface Props {
  instructionId: number;
  /** 库存结算草稿变化后加 1，用来重新读取对账表 */
  reloadKey?: number;
  onStatus?: (status: ContainersStatus) => void;
  /** 容器草稿保存后，让外层刷新（保存会让库存结算已有的预览确认作废） */
  onChanged?: () => void;
  /** 执行阶段的容器标签：只作参考，可以"参考新建"容器（只预填尺寸、重量、位置文字、备注，不预填内容，也不建立任何对应关系） */
  labels?: NonStdLabel[];
}

interface LocalItem { key: string; unit_key: string; qty: number }
interface LocalBox extends Omit<ContainerDraft, 'items'> { key: string; items: LocalItem[] }

let seed = 0;
const nextKey = () => `k${++seed}`;

const errorText = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return String(detail[0] || fallback);
  return String(detail || err?.message || fallback);
};

/** 草稿里的一行内容 → 对账表里的库存单元 key（还不存在的项目 AIU 是 aiu:<项目 id>；已经存在了就用真实的库存单元 id） */
const keyOfItem = (item: { iu_id?: number | null; aiu_project_id?: number | null }, ledger: ContainerLedgerRow[]): string => {
  if (item.iu_id) return String(item.iu_id);
  const key = `aiu:${item.aiu_project_id}`;
  if (ledger.some(row => row.unit_key === key)) return key;
  const real = ledger.find(row => row.unit_type === 'AIU' && row.project_id === item.aiu_project_id);
  return real ? real.unit_key : key;
};

const toLocal = (view: ContainersView): LocalBox[] => (view.containers || []).map(box => ({
  ...box,
  key: nextKey(),
  items: (box.items || []).map(item => ({ key: nextKey(), unit_key: keyOfItem(item, view.ledger || []), qty: item.qty })),
}));

const toPayload = (boxes: LocalBox[]): ContainerDraft[] => boxes.map(box => ({
  kind: box.kind,
  container_type: box.container_type,
  // 尺寸和重量只有 IOC 需要
  length_cm: box.kind === 'ioc' ? (box.length_cm ?? null) : null,
  width_cm: box.kind === 'ioc' ? (box.width_cm ?? null) : null,
  height_cm: box.kind === 'ioc' ? (box.height_cm ?? null) : null,
  weight_g: box.kind === 'ioc' ? (box.weight_g ?? null) : null,
  location_text: (box.location_text || '').trim(),
  remarks: box.remarks || '',
  reference_label_id: box.reference_label_id ?? null,
  items: box.items.map(item => (/^\d+$/.test(item.unit_key)
    ? { iu_id: Number(item.unit_key), qty: item.qty }
    : { aiu_project_id: Number(item.unit_key.replace('aiu:', '')), qty: item.qty })),
}));

/** 一行内容项：产品名(编码 条码)×数量 */
const contentLine = (it: { name: string; qty: number; code?: string; barcodes?: string[] }) => {
  const codes = it.barcodes || [];
  const parts = [it.code ? `编码:${it.code}` : '', codes.length ? `条码:${codes[0]}${codes.length > 1 ? ` 等${codes.length}个` : ''}` : ''].filter(Boolean);
  return `${it.name}${parts.length ? `(${parts.join(' ')})` : ''}×${it.qty}`;
};

/**
 * 终审容器分配：把执行完毕后仍在库的库存单元手动分配到新建的 SC / IOC 里。
 * 上面是对账表（每个库存单元需要分配多少、已分配多少），下面是容器卡片。保存草稿后，点“终审操作”区块最底部的“预览并确认终审操作”预览并确认，提交终审时由后端一次性创建。
 */
const ContainersPanel = forwardRef<ContainersPanelHandle, Props>(({ instructionId, reloadKey = 0, onStatus, onChanged, labels = [] }, ref) => {
  const [view, setView] = useState<ContainersView | null>(null);
  const [boxes, setBoxes] = useState<LocalBox[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState<Array<{ id: number; code: string }>>([]);
  const dirtyRef = useRef(false);
  // 参考标签列表是否收起（默认收起）
  const [labelsCollapsed, setLabelsCollapsed] = useState(true);
  const warehouseRef = useRef('');

  const editable = !!view?.can_edit;
  const ledger = useMemo(() => view?.ledger || [], [view]);

  const markDirty = (value: boolean) => { dirtyRef.current = value; setDirty(value); };

  const load = useCallback(async () => {
    try {
      const data = await getNonStdContainers(instructionId);
      setView(data);
      // 有没保存的修改时只更新对账表和版本，不覆盖正在编辑的容器
      if (!dirtyRef.current) setBoxes(toLocal(data));
      setError('');
    } catch (err: any) {
      setError(errorText(err, '加载终审容器失败'));
    }
  }, [instructionId]);

  useEffect(() => { if (instructionId) void load(); }, [instructionId, reloadKey, load]);

  // 本指令仓库里的仓位编码：只用来在仓位文字旁提示“正式仓位 / 临时位置”，不做下拉（只在可编辑时需要）
  useEffect(() => {
    const code = view?.warehouse_code || '';
    if (!view?.can_edit || !code || warehouseRef.current === code) return;
    warehouseRef.current = code;
    getLocations({ warehouse_code: code })
      .then(list => setLocations(list.map((l: any) => ({ id: Number(l.id), code: String(l.code) }))))
      .catch(() => setLocations([]));
  }, [view?.can_edit, view?.warehouse_code]);

  // 对账：用本地正在编辑的容器算已分配（服务器给的是上次保存的结果）
  const assigned = useMemo(() => {
    const map: Record<string, { sc: number; ioc: number }> = {};
    boxes.forEach(box => box.items.forEach(item => {
      const qty = Number(item.qty) || 0;
      if (!item.unit_key || qty <= 0) return;
      if (!map[item.unit_key]) map[item.unit_key] = { sc: 0, ioc: 0 };
      map[item.unit_key][box.kind] += qty;
    }));
    return map;
  }, [boxes]);

  const rows: ContainerLedgerRow[] = useMemo(() => {
    const known = new Set(ledger.map(row => row.unit_key));
    const list = ledger.map(row => {
      const sc = assigned[row.unit_key]?.sc || 0;
      const ioc = assigned[row.unit_key]?.ioc || 0;
      return { ...row, sc_qty: sc, ioc_qty: ioc, unassigned: row.total - sc - ioc };
    });
    Object.entries(assigned).forEach(([key, value]) => {
      if (known.has(key)) return;
      list.push({
        unit_key: key, iu_id: /^\d+$/.test(key) ? Number(key) : null, iu_code: key, unit_type: '', new_unit: false, locked: 0, picked: null,
        convert: 0, destroy: 0, kept: 0, produced: 0, total: 0, hold: 0, sc_qty: value.sc, ioc_qty: value.ioc, unassigned: -(value.sc + value.ioc),
      });
    });
    return list;
  }, [ledger, assigned]);

  const rowOf = useCallback((key: string) => rows.find(row => row.unit_key === key), [rows]);
  /** 这个库存单元还需要放进某种容器的数量 */
  const needOf = useCallback((row: ContainerLedgerRow, kind: ContainerKind) => (
    kind === 'ioc' ? row.hold - row.ioc_qty : (row.total - row.hold) - row.sc_qty
  ), []);

  // 向外报告状态
  const count = view?.containers?.length || 0;
  const hasBlockers = !!view?.engaged && (view?.blockers?.length || 0) > 0;
  const statusKey = `${!view ? 'loading' : ''}|${view?.applied}|${editable}|${dirty}|${count}|${hasBlockers}`;
  useEffect(() => {
    if (!onStatus || !view) return;
    if (!view.supported) onStatus({ text: '不支持', color: 'default', dirty: false, count: 0, hasBlockers: false });
    else if (view.applied) onStatus({ text: '容器已生成', color: 'success', dirty: false, count, hasBlockers: false });
    else if (!view.can_edit) onStatus({ text: '提交终审时不新建容器', color: 'default', dirty: false, count, hasBlockers: false });
    else if (dirty) onStatus({ text: '容器草稿有未保存的修改', color: 'warning', dirty: true, count, hasBlockers });
    else if (hasBlockers) onStatus({ text: '容器分配有问题', color: 'warning', dirty: false, count, hasBlockers: true });
    else if (count > 0) onStatus({ text: `已保存 ${count} 个容器`, color: 'processing', dirty: false, count, hasBlockers: false });
    else onStatus({ text: '没有建容器：提交终审时不新建容器', color: 'default', dirty: false, count: 0, hasBlockers: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey]);

  // ---------- 编辑动作 ----------
  const change = (updater: (prev: LocalBox[]) => LocalBox[]) => {
    setBoxes(prev => updater(prev));
    markDirty(true);
  };
  const patchBox = (key: string, patch: Partial<LocalBox>) => change(prev => prev.map(box => (box.key === key ? { ...box, ...patch } : box)));
  const patchItem = (boxKey: string, itemKey: string, patch: Partial<LocalItem>) => change(prev => prev.map(box => (
    box.key === boxKey ? { ...box, items: box.items.map(item => (item.key === itemKey ? { ...item, ...patch } : item)) } : box
  )));
  const newBox = (kind: ContainerKind, items: LocalItem[] = []): LocalBox => ({
    key: nextKey(), kind, container_type: 'box', items,
  });
  const addBox = (kind: ContainerKind) => change(prev => [...prev, newBox(kind)]);

  /** 参考执行阶段的标签新建容器：只预填尺寸、重量、位置、备注和参考的标签，内容全部由管理员自己分配 */
  const addBoxFromLabel = (label: NonStdLabel, kind: ContainerKind) => {
    const whole = (value: number | string | null | undefined) => {
      const n = Number(value);
      return value === null || value === undefined || value === '' || !Number.isFinite(n) || n < 0 ? null : Math.round(n);
    };
    // 标签上的位置原样填进仓位文字（IOC 和 SC 一样）：和仓位表编码一致就是正式仓位，否则是临时位置，提交时由系统解析
    const place = (label.location || '').trim().slice(0, 64);
    const name = `${label.group ? `${label.group}-` : ''}${label.number || label.id}`;
    const note = `参考标签 ${name}${label.contents ? `：${label.contents}` : ''}${label.remark ? `（${label.remark}）` : ''}`.slice(0, 255);
    const box: LocalBox = {
      ...newBox(kind),
      // 尺寸和重量只有 IOC 需要（SC 不填）
      ...(kind === 'ioc' ? { length_cm: whole(label.length_cm), width_cm: whole(label.width_cm), height_cm: whole(label.height_cm), weight_g: whole(label.weight_g) } : {}),
      location_text: place,
      remarks: note,
      reference_label_id: label.id,
    };
    change(prev => [...prev, box]);
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      message.success('已复制');
    } catch {
      message.warning('复制失败，请手动复制');
    }
  };
  // 终审提交后生成的 SC / IOC：打印容器标签（和仓位表页面同一套标签）
  const labelPrinter = useContainerLabelPrinter();
  const removeBox = (key: string) => change(prev => prev.filter(box => box.key !== key));
  const addItem = (boxKey: string) => change(prev => prev.map(box => (
    box.key === boxKey ? { ...box, items: [...box.items, { key: nextKey(), unit_key: '', qty: 1 }] } : box
  )));
  const removeItem = (boxKey: string, itemKey: string) => change(prev => prev.map(box => (
    box.key === boxKey ? { ...box, items: box.items.filter(item => item.key !== itemKey) } : box
  )));

  /** 把还没分配完的数量全部装进这个容器（SC 只装同一个分拣组的） */
  const fillRemaining = (boxKey: string) => {
    const box = boxes.find(b => b.key === boxKey);
    if (!box) return;
    const existing = box.items.map(item => rowOf(item.unit_key)).find(Boolean);
    const candidates = rows.filter(row => needOf(row, box.kind) > 0);
    const group = box.kind === 'sc' ? (existing?.group ?? candidates[0]?.group) : undefined;
    const picks = candidates.filter(row => box.kind === 'ioc' || row.group === group);
    if (picks.length === 0) { message.info('没有需要继续分配的数量了'); return; }
    change(prev => prev.map(b => {
      if (b.key !== boxKey) return b;
      const items = b.items.map(item => ({ ...item }));
      picks.forEach(row => {
        const need = needOf(row, box.kind);
        const same = items.find(item => item.unit_key === row.unit_key);
        if (same) same.qty = (Number(same.qty) || 0) + need;
        else items.push({ key: nextKey(), unit_key: row.unit_key, qty: need });
      });
      return { ...b, items };
    }));
  };

  /** 按对账表自动生成：转业务留存占用的装一个 IOC，其余按分拣组各装一个 SC */
  const autoGenerate = () => {
    const generated: LocalBox[] = [];
    const holds = rows.filter(row => row.hold > 0);
    if (holds.length > 0) generated.push(newBox('ioc', holds.map(row => ({ key: nextKey(), unit_key: row.unit_key, qty: row.hold }))));
    const byGroup = new Map<string, ContainerLedgerRow[]>();
    rows.filter(row => row.total - row.hold > 0).forEach(row => {
      const list = byGroup.get(row.group || row.unit_key) || [];
      list.push(row);
      byGroup.set(row.group || row.unit_key, list);
    });
    byGroup.forEach(list => generated.push(newBox('sc', list.map(row => ({ key: nextKey(), unit_key: row.unit_key, qty: row.total - row.hold })))));
    if (generated.length === 0) { message.info('没有需要分配到容器的库存'); return; }
    change(() => generated);
  };

  const problems = useMemo(() => {
    for (const box of boxes) {
      if (box.items.length === 0) return '有容器没有内容，请添加内容或删除这个容器';
      if (box.items.some(item => !item.unit_key)) return '有内容行还没有选择库存单元';
      if (box.items.some(item => !(Number(item.qty) >= 1))) return '内容的数量必须大于 0';
    }
    return '';
  }, [boxes]);

  const save = async (silent = false): Promise<boolean> => {
    if (problems) { message.warning(problems); return false; }
    if (!view) return false;
    setBusy(true);
    try {
      const data = await saveNonStdContainers(instructionId, { version: view.version, containers: toPayload(boxes) });
      setView(data);
      setBoxes(toLocal(data));
      markDirty(false);
      if (!silent) message.success('容器草稿已保存，请点下面的“预览并确认终审操作”');
      onChanged?.();
      return true;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        message.warning('容器草稿已被别人修改，已重新加载最新的内容，请检查后再改');
        markDirty(false);
        await load();
      } else {
        message.error(errorText(err, '保存失败'));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => { markDirty(false); await load(); };

  useImperativeHandle(ref, () => ({
    save: async (silent = true) => (dirty ? save(silent) : true),
    refresh: load,
  }));

  // ---------- 展示 ----------
  if (error && !view) return <Alert type="error" showIcon message={error} />;
  if (!view) return <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>;
  if (!view.supported) return <Alert type="info" showIcon message={view.unsupported_reason || '这个指令不支持终审容器分配'} />;

  const unitLabel = (row: ContainerLedgerRow) => (
    <div>
      <div><b>{row.iu_code}</b>{row.new_unit ? <Tag color="blue" style={{ marginLeft: 6 }}>将创建</Tag> : null}</div>
      {(row.content_items || []).map((it, i) => <div key={i} style={{ fontSize: 12, color: '#595959' }}>{contentLine(it)}</div>)}
    </div>
  );

  /** 需分配数量的计算过程：拣出 − 转为新库存单元 − 销毁 ＋ 新增；只写数字不是 0 的项（没拣出的库存单元不写转化和销毁） */
  const formulaOf = (row: ContainerLedgerRow) => {
    const picked = Number(row.picked) || 0;
    let text = '';
    if (picked > 0) {
      text = `拣出${picked}`;
      if (row.convert > 0) text += ` − 转为新库存单元${row.convert}`;
      if (row.destroy > 0) text += ` − 销毁${row.destroy}`;
      // 转化和销毁超过了拣出的数量：保留的部分按 0 计（预览里另有警告）
      if (picked - row.convert - row.destroy < 0) text += '（不足，按0计）';
    }
    if (row.produced > 0) text += `${text ? ' + ' : ''}新增${row.produced}`;
    return text;
  };

  /** 仓位文字旁的小标签：告诉你系统会怎么理解（提交终审时以后端按仓位表解析的结果为准） */
  const locationTag = (box: LocalBox) => {
    const text = (box.location_text || '').trim();
    if (!text || locations.length === 0) return null;
    const formal = locations.some(l => l.code.toLowerCase() === text.toLowerCase());
    if (formal) return <Tag color="green" className="nonstd-containers-loc-tag" style={{ marginLeft: 6 }}>正式仓位</Tag>;
    if (box.kind === 'sc' && box.container_type === 'direct') return <Tag color="red" className="nonstd-containers-loc-tag" style={{ marginLeft: 6 }}>直接存储必须是仓位表里已有的仓位</Tag>;
    if (box.kind === 'sc') {
      return (
        <Tooltip title="SC 只能挂仓位表里的正式仓位：这个位置没有匹配的仓位，生成的 SC 仍是“未上架”，位置文字会记在备注和移动记录里，以后再走正常的上架流程。">
          <Tag color="orange" className="nonstd-containers-loc-tag" style={{ marginLeft: 6 }}>临时仓位：将记为未上架</Tag>
        </Tooltip>
      );
    }
    return <Tag color="orange" className="nonstd-containers-loc-tag" style={{ marginLeft: 6 }}>临时仓位</Tag>;
  };

  /** 限定宽度的文字：太长用 ... 代替，鼠标移上去显示完整内容 */
  const clipped = (text: string | null | undefined, className: string) => {
    const value = (text || '').trim();
    if (!value) return '-';
    return (
      <Tooltip title={value} overlayStyle={{ maxWidth: 420 }}>
        <div className={className} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      </Tooltip>
    );
  };

  /** 列头 + 问号提示（说明这一列的含义） */
  const headTip = (text: string, tip: React.ReactNode) => (
    <span>
      {text}
      <Tooltip title={tip} placement="bottom" overlayStyle={{ maxWidth: 440 }}>
        <QuestionCircleOutlined className="nonstd-containers-head-help" style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'help' }} />
      </Tooltip>
    </span>
  );

  const ledgerTable = (
    <Table
      className="nonstd-containers-ledger"
      size="small"
      rowKey="unit_key"
      pagination={false}
      dataSource={rows}
      scroll={{ x: 1100 }}
      // 已经全部分配完的行用浅绿色背景
      onRow={(row: ContainerLedgerRow) => ({ className: row.total > 0 && row.unassigned === 0 ? 'nonstd-containers-done' : '', style: row.total > 0 && row.unassigned === 0 ? { background: '#f6ffed' } : undefined })}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="执行完毕后没有需要分配到新容器的库存" /> }}
      columns={[
        {
          title: '库存单元',
          width: 170,
          render: (_: any, row) => <span><b className="nonstd-containers-code">{row.iu_code}</b>{row.new_unit ? <Tag color="blue" style={{ marginLeft: 6 }}>将创建</Tag> : null}</span>,
        },
        {
          title: '库存单元内容项',
          width: 420,
          render: (_: any, row) => ((row.content_items || []).length === 0
            ? '-'
            : <div className="nonstd-containers-contents" style={{ fontSize: 12 }}>{(row.content_items || []).map((it, i) => <div key={i}>{contentLine(it)}</div>)}</div>),
        },
        {
          title: headTip('需分配数量', (
            <div>
              <div>执行完毕后，这个库存单元要放进新容器的数量。分两类：</div>
              <div style={{ marginTop: 6 }}><b>指令锁定的库存单元（操作对象）</b>：需分配数量 = 拣出数量 − 转为新库存单元的数量 − 销毁的数量，也就是拣出后保留下来的部分。拣货不要求拣满，所以按拣出数量算，不按锁定数量算；没有拣出的部分仍在原容器里，不需要分配。</div>
              <div style={{ marginTop: 6 }}><b>新转化成的库存单元（转化目标，含项目异常库存 AIU）</b>：需分配数量 = 库存结算里转为这个库存单元的新增数量。</div>
              <div style={{ marginTop: 6 }}>同一个库存单元既是操作对象又是转化目标时，两部分相加。</div>
            </div>
          )),
          width: 170,
          render: (_: any, row) => (
            <div>
              <b className="nonstd-containers-total">{row.total}</b>
              <div className="nonstd-containers-formula" style={{ fontSize: 12, color: '#8c8c8c' }}>{formulaOf(row)}</div>
            </div>
          ),
        },
        {
          title: headTip('已分配入仓储容器SC', (
            <div>
              <div>这个库存单元已经放进所有仓储容器（SC）里的数量合计。</div>
              <div style={{ marginTop: 6 }}>SC 可以装保留的和新转化的库存单元；同一个 SC 里的库存单元必须属于同一个分拣组（项目异常库存 AIU 要单独放一个 SC）。</div>
              <div style={{ marginTop: 6 }}>需分配数量里，扣掉放进 IOC 的部分，剩下的放进 SC。</div>
            </div>
          )),
          dataIndex: 'sc_qty',
          width: 170,
        },
        {
          title: headTip('已分配入指令产出容器IOC', (
            <div>
              <div>这个库存单元已经放进所有指令产出容器（IOC）里的数量合计。</div>
              <div style={{ marginTop: 6 }}>IOC 只装指令锁定的库存单元，并且<b>数量必须正好等于库存结算里这个库存单元的“转业务留存占用”数量</b>：转业务留存占用的库存，在账上继续被本指令占用，所以必须放进 IOC，后续指令（例如退回供货商的出库）才能选用这个 IOC；没有转业务留存占用的库存单元不能放进 IOC。</div>
              <div style={{ marginTop: 6 }}>数量和转业务留存占用不一致时，这里显示红色。</div>
            </div>
          )),
          dataIndex: 'ioc_qty',
          width: 190,
          render: (v: number, row) => (
            <div>
              <span className="nonstd-containers-ioc-qty" style={v !== row.hold ? { color: '#cf1322', fontWeight: 600 } : undefined}>{v}</span>
              {v !== row.hold ? <div className="nonstd-containers-ioc-expect" style={{ fontSize: 12, color: '#cf1322' }}>应为转业务留存占用 {row.hold}</div> : null}
            </div>
          ),
        },
        {
          title: '未分配',
          dataIndex: 'unassigned',
          width: 90,
          fixed: 'right' as const, // 横向滚动时始终显示在右侧
          // 固定列的单元格自带白色底，行的浅绿色透不过来：分配完的行在这一格上也设成浅绿色
          onCell: (row: ContainerLedgerRow) => ({ style: row.total > 0 && row.unassigned === 0 ? { background: '#f6ffed' } : undefined }),
          render: (v: number) => <span className="nonstd-containers-unassigned" style={v !== 0 ? { color: '#cf1322', fontWeight: 600 } : { color: '#389e0d' }}>{v < 0 ? `多分 ${-v}` : v}</span>,
        },
      ]}
    />
  );

  const boxCard = (box: LocalBox, index: number) => {
    const isIoc = box.kind === 'ioc';
    const disabled = !editable;
    const mine = box.items.map(item => rowOf(item.unit_key)).find(Boolean);
    // 这个容器还能不能再放东西：还需要放进这种容器的库存单元，SC 里只能是同一个分拣组的；放不下了就禁用按钮并说明原因
    const isAiuRow = (row: ContainerLedgerRow) => row.unit_type === 'AIU' || row.unit_key.startsWith('aiu:');
    const compatible = (row: ContainerLedgerRow) => isIoc || !mine || row.group === mine.group;
    const stillNeeded = rows.filter(row => needOf(row, box.kind) > 0);
    const fillCandidates = stillNeeded.filter(compatible);
    const addCandidates = fillCandidates.filter(row => !box.items.some(item => item.unit_key === row.unit_key));
    const noRoomReason = (): string => {
      if (isIoc) {
        return rows.some(row => row.hold > 0)
          ? '转业务留存占用的库存已经全部放进 IOC 了：IOC 里每个库存单元的数量必须正好等于库存结算里的“转业务留存占用”，不能再多放。'
          : '库存结算里没有设置“转业务留存占用”的库存单元：IOC 只装转业务留存占用的操作对象，没有可以放进来的内容。';
      }
      if (stillNeeded.length === 0) return '需分配的数量已经全部分配完了（或者都已经分配给了 IOC），没有可以再放进 SC 的库存。';
      if (mine && isAiuRow(mine)) return '这个 SC 里装的是项目异常库存（AIU）：AIU 要单独放一个 SC，不能和其他库存单元混装。还没分配完的库存单元请新建一个 SC 来装。';
      if (stillNeeded.every(isAiuRow)) return '剩下还没分配完的是项目异常库存（AIU）：AIU 要单独放一个 SC，不能和这个 SC 里的库存单元混装。请新建一个 SC 来装。';
      return '同一个 SC 里的库存单元必须属于同一个分拣组：这个 SC 里已有的库存单元和剩下还没分配完的库存单元不是同一个分拣组。请新建一个 SC 来装。';
    };
    const addDisabledReason = addCandidates.length > 0 ? '' : (
      fillCandidates.length > 0
        ? '能放进这个容器的库存单元都已经在里面了。数量不够的话，直接修改数量，或者点“装入全部剩余”补齐。'
        : noRoomReason()
    );
    const fillDisabledReason = fillCandidates.length > 0 ? '' : noRoomReason();
    // 每一行的下拉：还需要分配的库存单元（SC 里只能是同一个分拣组的），本容器里别的行已经选了的不再出现，自己当前选的保留
    const optionsFor = (itemKey: string) => rows
      .filter(row => {
        if (box.items.some(item => item.key === itemKey && item.unit_key === row.unit_key)) return true;
        if (box.items.some(item => item.key !== itemKey && item.unit_key === row.unit_key)) return false;
        if (needOf(row, box.kind) <= 0) return false;
        return isIoc || !mine || row.group === mine.group;
      })
      .map(row => ({
        value: row.unit_key,
        label: unitLabel(row),
        short: row.iu_code,
        search: `${row.iu_code} ${row.project_code || ''} ${(row.content_items || []).map(contentLine).join(' ')}`.toLowerCase(),
      }));
    return (
      <Card
        key={box.key}
        className={`nonstd-containers-box nonstd-containers-${box.kind}`}
        size="small"
        style={{ marginBottom: 10 }}
        title={(
          <Space size={8}>
            <span className="nonstd-containers-no">第 {index + 1} 个</span>
            <Tag color={isIoc ? 'red' : 'blue'} className="nonstd-containers-kind">{isIoc ? 'IOC 指令产出容器' : 'SC 仓储容器'}</Tag>
            {box.applied_container_code ? (
              <>
                <Tag color="success" className="nonstd-containers-applied">已生成 {box.applied_container_code}</Tag>
                <Button size="small" type="text" aria-label="复制容器编码" className="nonstd-containers-copy" icon={<CopyOutlined />} onClick={() => { void copyCode(box.applied_container_code || ''); }} />
                {box.applied_container_id ? (
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    className="nonstd-containers-print-label"
                    loading={labelPrinter.loadingId === box.applied_container_id}
                    onClick={() => { void labelPrinter.openLabel({ kind: box.kind, id: Number(box.applied_container_id), code: box.applied_container_code || '' }); }}
                  >
                    打印标签
                  </Button>
                ) : null}
              </>
            ) : null}
            {box.reference_label_id && !box.applied_container_code ? <Tag className="nonstd-containers-ref">参考执行阶段标签</Tag> : null}
          </Space>
        )}
        extra={editable ? <Button size="small" danger type="text" icon={<DeleteOutlined />} aria-label="删除容器" className="nonstd-containers-remove" onClick={() => removeBox(box.key)} /> : null}
      >
        <div className="nonstd-containers-row1" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 12, marginBottom: 8, overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <span>
            类型{' '}
            <Select size="small" style={{ width: 100 }} disabled={disabled} value={box.container_type} options={CONTAINER_TYPES} onChange={v => patchBox(box.key, { container_type: v })} />
          </span>
          <span>
            仓位{' '}
            <Input
              size="small"
              className="nonstd-containers-location"
              style={{ width: 130 }}
              maxLength={64}
              disabled={disabled}
              placeholder="填仓位编码或临时位置"
              value={box.location_text || ''}
              onChange={e => patchBox(box.key, { location_text: e.target.value })}
            />
            {locationTag(box)}
          </span>
          <Input size="small" style={{ flex: '1 1 100px', minWidth: 90, maxWidth: 200 }} maxLength={255} disabled={disabled} placeholder="备注（选填）" value={box.remarks || ''} onChange={e => patchBox(box.key, { remarks: e.target.value })} />
        </div>
        {isIoc ? (
          <Space wrap size={[12, 8]} style={{ marginBottom: 8 }} className="nonstd-containers-ioc-size">
            <span>
              尺寸(cm){' '}
              <InputNumber size="small" style={{ width: 70 }} min={0} precision={0} disabled={disabled} placeholder="长" value={box.length_cm ?? null} onChange={v => patchBox(box.key, { length_cm: v === null ? null : Number(v) })} />
              {' × '}
              <InputNumber size="small" style={{ width: 70 }} min={0} precision={0} disabled={disabled} placeholder="宽" value={box.width_cm ?? null} onChange={v => patchBox(box.key, { width_cm: v === null ? null : Number(v) })} />
              {' × '}
              <InputNumber size="small" style={{ width: 70 }} min={0} precision={0} disabled={disabled} placeholder="高" value={box.height_cm ?? null} onChange={v => patchBox(box.key, { height_cm: v === null ? null : Number(v) })} />
            </span>
            <span>
              重量(g){' '}
              <InputNumber size="small" style={{ width: 90 }} min={0} precision={0} disabled={disabled} value={box.weight_g ?? null} onChange={v => patchBox(box.key, { weight_g: v === null ? null : Number(v) })} />
            </span>
          </Space>
        ) : null}
        <Table
          className="nonstd-containers-items"
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={box.items}
          locale={{ emptyText: '还没有内容' }}
          columns={[
            {
              title: '库存单元',
              render: (_: any, item: LocalItem) => (
                <Select
                  size="small"
                  showSearch
                  style={{ width: '100%', minWidth: 260 }}
                  disabled={disabled}
                  placeholder="选择库存单元"
                  optionLabelProp="short"
                  filterOption={(input, option) => String((option as any)?.search || '').includes(input.toLowerCase())}
                  value={item.unit_key || undefined}
                  options={optionsFor(item.key)}
                  onChange={v => patchItem(box.key, item.key, { unit_key: v })}
                />
              ),
            },
            {
              title: isIoc ? '数量（= 转业务留存）' : '数量',
              width: 150,
              render: (_: any, item: LocalItem) => (
                <InputNumber size="small" className="nonstd-containers-qty" style={{ width: 100 }} min={1} precision={0} disabled={disabled} value={item.qty} onChange={v => patchItem(box.key, item.key, { qty: Number(v) || 0 })} />
              ),
            },
            {
              title: '',
              width: 50,
              render: (_: any, item: LocalItem) => (editable ? <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="删除内容" onClick={() => removeItem(box.key, item.key)} /> : null),
            },
          ]}
        />
        {editable ? (
          <Space style={{ marginTop: 8 }}>
            {/* 按钮禁用时不能直接挂提示，外面包一层让鼠标移上去能看到原因 */}
            <Tooltip title={addDisabledReason || undefined} overlayStyle={{ maxWidth: 380 }}>
              <span className="nonstd-containers-add-item-tip">
                <Button size="small" icon={<PlusOutlined />} className="nonstd-containers-add-item" disabled={!!addDisabledReason} onClick={() => addItem(box.key)}>添加内容</Button>
              </span>
            </Tooltip>
            <Tooltip title={fillDisabledReason || undefined} overlayStyle={{ maxWidth: 380 }}>
              <span className="nonstd-containers-fill-tip">
                <Button size="small" className="nonstd-containers-fill" disabled={!!fillDisabledReason} onClick={() => fillRemaining(box.key)}>装入全部剩余</Button>
              </span>
            </Tooltip>
          </Space>
        ) : null}
      </Card>
    );
  };

  /** 三个子块的标题：一条带标题的分隔线（标题靠左，加粗），把"待分配的库存单元""执行阶段的标签""最终的容器列表"明显分开 */
  const blockTitle = (text: string, className: string, first = false, extra: React.ReactNode = null) => (
    <Divider
      orientation="left"
      orientationMargin={0}
      className={`nonstd-containers-block-title ${className}`}
      style={{ margin: first ? '0 0 10px' : '22px 0 10px', borderBlockStartColor: '#bfbfbf' }}
    >
      <span style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>{text}</span>
      {extra}
    </Divider>
  );

  return (
    <div className="nonstd-containers-panel">
      {blockTitle('待分配的库存单元', 'nonstd-containers-block-ledger', true)}
      {view.applied && view.ledger_snapshot_missing ? (
        <div className="nonstd-containers-snapshot-note" style={{ color: '#8c8c8c', fontSize: 12 }}>
          这个指令是在保存对账结果之前提交的，没有提交当时的对账表；生成的容器见下面。
        </div>
      ) : (
        <>
          {view.applied && view.ledger_snapshot ? (
            <div className="nonstd-containers-snapshot-note" style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>
              以下是提交终审当时的对账结果（已保存的快照，不会再随库存和锁定的变化而变化）。
            </div>
          ) : null}
          {ledgerTable}
        </>
      )}

      {view.engaged ? (view.blockers || []).map((b, i) => <Alert key={`b${i}`} className="nonstd-containers-blocker" type="error" showIcon style={{ marginTop: 6 }} message={`阻断：${b.message}`} />) : null}
      {view.engaged ? (view.warnings || []).map((w, i) => <Alert key={`w${i}`} className="nonstd-containers-warning" type="warning" showIcon style={{ marginTop: 6 }} message={`警告：${w.message}`} />) : null}

      {editable && labels.length > 0 ? (() => {
        // 已消耗（容器已不存在）、未使用（打印了标签没装东西）的标签没有对应的真实容器：灰色显示，不能参考新建
        const inactiveReason = (label: NonStdLabel) => (label.consumed
          ? '已消耗的容器已不存在，不能参考新建'
          : label.unused ? '未使用的容器（打印了标签但没有装东西），不能参考新建' : '');
        const consumedCount = labels.filter(label => label.consumed).length;
        const unusedCount = labels.filter(label => !label.consumed && label.unused).length;
        const inactiveText = [consumedCount ? `已消耗 ${consumedCount} 个` : '', unusedCount ? `未使用 ${unusedCount} 个` : ''].filter(Boolean).join('，');
        const refButton = (label: NonStdLabel, kind: ContainerKind) => {
          const text = kind === 'sc' ? '参考新建 SC' : '参考新建 IOC';
          const reason = inactiveReason(label);
          if (!reason) return <Button size="small" className={`nonstd-containers-ref-${kind}`} onClick={() => addBoxFromLabel(label, kind)}>{text}</Button>;
          // 按钮禁用时不能直接挂提示，外面包一层让鼠标移上去能看到原因
          return (
            <Tooltip title={reason}>
              <span className="nonstd-containers-ref-tip" style={{ display: 'block' }}>
                <Button size="small" block disabled className={`nonstd-containers-ref-${kind}`}>{text}</Button>
              </span>
            </Tooltip>
          );
        };
        return (
        <div className="nonstd-containers-labels">
          {blockTitle('执行阶段的标签（参考）', 'nonstd-containers-block-labels', false, (
            <Button
              size="small"
              type="text"
              className="nonstd-containers-labels-toggle"
              style={{ marginLeft: 4 }}
              aria-label={labelsCollapsed ? '展开标签列表' : '收起标签列表'}
              icon={labelsCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
              onClick={() => setLabelsCollapsed(value => !value)}
            />
          ))}
          {!labelsCollapsed ? (
            <Table
              size="small"
              rowKey="id"
              pagination={{
                defaultPageSize: 5,
                size: 'small',
                hideOnSinglePage: true,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20'],
                showTotal: total => `共 ${total} 个标签${inactiveText ? `（${inactiveText}）` : ''}`,
              }}
              dataSource={labels}
              onRow={(label: NonStdLabel) => ({ className: inactiveReason(label) ? 'nonstd-containers-label-inactive' : '', style: inactiveReason(label) ? { color: '#8c8c8c', background: '#fafafa' } : undefined })}
              columns={[
                { title: <>标签<br />分组与序号</>, width: 120, render: (_: any, label: NonStdLabel) => `${label.group ? `${label.group}-` : ''}${label.number || label.id}` },
                { title: '内容物', width: 200, render: (_: any, label: NonStdLabel) => clipped(label.contents, 'nonstd-containers-label-contents') },
                { title: '备注', width: 200, render: (_: any, label: NonStdLabel) => clipped(label.remark, 'nonstd-containers-label-remark') },
                {
                  title: '尺寸/重量',
                  width: 150,
                  render: (_: any, label: NonStdLabel) => {
                    const num = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '-' : Number(v));
                    return (
                      <div className="nonstd-containers-label-size">
                        <div>{[label.length_cm, label.width_cm, label.height_cm].map(num).join('×')} cm</div>
                        <div>{num(label.weight_g)} g</div>
                      </div>
                    );
                  },
                },
                {
                  title: '位置',
                  dataIndex: 'location',
                  width: 110,
                  render: (v: string, label: NonStdLabel) => (label.consumed
                    ? <ConsumedTag />
                    : label.unused
                      ? <Tooltip title="员工打印了这个标签，但容器没有装东西"><Tag style={{ color: '#8c8c8c', background: '#f0f0f0', borderColor: '#d9d9d9', marginInlineEnd: 0 }}>未使用</Tag></Tooltip>
                      : v || '-'),
                },
                {
                  title: '操作',
                  width: 130,
                  render: (_: any, label: NonStdLabel) => (
                    <div className="nonstd-containers-label-actions" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {refButton(label, 'sc')}
                      {refButton(label, 'ioc')}
                    </div>
                  ),
                },
              ]}
            />
          ) : null}
        </div>
        );
      })() : null}

      {blockTitle('最终的容器列表', 'nonstd-containers-block-boxes')}
      <div>
        {boxes.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={view.applied ? '本指令没有新建容器' : '还没有建容器'} />
        ) : boxes.map(boxCard)}
      </div>

      {editable ? (
        <Space wrap style={{ marginTop: 10 }}>
          <Button icon={<PlusOutlined />} className="nonstd-containers-add-sc" onClick={() => addBox('sc')}>新建 SC</Button>
          <Button icon={<PlusOutlined />} className="nonstd-containers-add-ioc" onClick={() => addBox('ioc')}>新建 IOC</Button>
          {boxes.length === 0 && rows.length > 0 ? <Button className="nonstd-containers-auto" onClick={autoGenerate}>按对账表自动生成</Button> : null}
          <Button type="primary" className="nonstd-containers-save" loading={busy} disabled={!dirty} onClick={() => { void save(); }}>保存容器草稿</Button>
          <Button disabled={!dirty} onClick={() => { void reset(); }}>放弃修改</Button>
          {dirty ? <Tag color="warning">有未保存的修改</Tag> : null}
        </Space>
      ) : null}
      {problems && dirty ? <Alert type="warning" showIcon style={{ marginTop: 8 }} message={problems} /> : null}
      {labelPrinter.modals}
    </div>
  );
});

export default ContainersPanel;
